import { computed, Injectable, signal } from "@angular/core";
import { defaultTab, ITab } from "@shared/models/tab";
import { Subject } from "rxjs";
import { Action, ITabSubjectAction } from "@shared/models/tab-subject-action";

@Injectable({
    providedIn: "root",
})
export class TabsStateService {
    private readonly _tabs = signal([defaultTab]);
    public tabs = computed(() => this._tabs().map((t) => ({ ...t })));

    tabActionSubject = new Subject<ITabSubjectAction>();

    public getTabById<TState = unknown>(tabId: number): ITab<TState> | undefined {
        const foundTab = this._tabs().find((t) => t.tabId === Number(tabId));
        return foundTab ? ({ ...foundTab } as ITab<TState>) : undefined;
    }

    public addTab(tab: ITab): void {
        tab.tabId = this.findAvailableMaxTabId();

        this._tabs.update((t) => [...t, tab]);
        this.tabActionSubject.next({ id: tab.tabId, action: Action.Add });
    }

    public addTabAfterId(tabId: string, tab: ITab): void {
        const index = this._tabs().findIndex((item) => item.tabId === +tabId);

        if (index !== -1) {
            tab.tabId = this.findAvailableMaxTabId();

            this._tabs.update((tabs) => {
                tabs.splice(index + 1, 0, tab);
                return [...tabs];
            });

            this.tabActionSubject.next({ id: tab.tabId, action: Action.Add });
        }
    }

    public activateTab(tab: ITab): void {
        this._tabs.update((tabs) =>
            tabs.map((t) => ({
                ...t,
                isActive: t.tabId === tab.tabId,
            })),
        );

        this.tabActionSubject.next({ id: tab.tabId, action: Action.Update });
    }

    public deleteTab(tabId: number): void {
        if (this._tabs().length === 1) return;

        this._tabs.update((tabs) => {
            return tabs.filter((t) => t.tabId !== tabId);
        });

        this.tabActionSubject.next({ action: Action.Delete });
    }

    public resetTabs(): void {
        this._tabs.set([defaultTab]);
        this.activateTab(defaultTab);

        this.tabActionSubject.next({ action: Action.Delete });
    }

    public findAvailableMaxTabId(): number {
        if (this._tabs().length === 0) return 0;

        const tabIds = this._tabs().map((t) => t.tabId);
        return Math.max(...tabIds) + 1;
    }

    public updateTab(tab: ITab): void {
        this._tabs.update((tabs) => {
            return tabs.map((t) => (t.tabId === tab.tabId ? tab : t));
        });
    }

    public getNextOrPreviousTab<TState = unknown>(tab: ITab<TState>): ITab<TState> {
        const currentIndex = this._tabs().findIndex((t) => t.tabId === tab.tabId);

        if (currentIndex === -1) {
            throw new RangeError("Unknown tab");
        }

        if (currentIndex < this._tabs().length - 1) {
            return { ...this._tabs()[currentIndex + 1] } as ITab<TState>;
        }

        if (currentIndex >= 0) {
            return { ...this._tabs()[currentIndex - 1] } as ITab<TState>;
        }

        return { ...tab };
    }

    public getActiveTab<TState = unknown>(): ITab<TState> | null {
        const activeTab = this._tabs().find((t) => t.isActive);
        return activeTab ? ({ ...activeTab } as ITab<TState>) : null;
    }
}
